import React, { useState, useEffect } from 'react';
import {
  MdArrowBack, MdAdd, MdSearch, MdLightbulbOutline, MdElectricBolt,
  MdLocalPhone, MdCreditCard, MdHomeWork, MdReceiptLong, MdMoreHoriz,
  MdChevronRight, MdEdit, MdDelete,
} from 'react-icons/md';
import { useProfile } from '../contexts/ProfileContext';

const ICON_FOR = (cat) => ({
  utility:   { Icon: MdElectricBolt,   tint: 'var(--td-amber)'  },
  internet:  { Icon: MdLightbulbOutline, tint: 'var(--td-blue)' },
  telecom:   { Icon: MdLocalPhone,     tint: 'var(--td-purple)' },
  card:      { Icon: MdCreditCard,     tint: 'var(--td-brown)'  },
  mortgage:  { Icon: MdHomeWork,       tint: 'var(--td-premium)'},
  other:     { Icon: MdReceiptLong,    tint: 'var(--td-gray)'   },
}[cat] || { Icon: MdMoreHoriz, tint: 'var(--td-gray)' });

const INITIAL_PAYEES = [
  { id: 'p1', name: 'Toronto Hydro',        nickname: 'Apartment hydro',  acct: '••••5421', cat: 'utility',  lastPaid: '2026-04-22', lastAmount: 88.40 },
  { id: 'p2', name: 'Rogers Communications', nickname: 'Internet + Mobile',acct: '••••8810', cat: 'internet', lastPaid: '2026-04-15', lastAmount: 145.99 },
  { id: 'p3', name: 'Bell Canada',          nickname: 'Home phone',       acct: '••••2235', cat: 'telecom',  lastPaid: '2026-04-12', lastAmount: 41.95 },
  { id: 'p4', name: 'CIBC Visa',            nickname: 'Sarah\'s card',    acct: '••••9982', cat: 'card',     lastPaid: '2026-04-28', lastAmount: 320.00 },
  { id: 'p5', name: 'Enbridge Gas',         nickname: 'Gas',              acct: '••••1144', cat: 'utility',  lastPaid: '2026-03-30', lastAmount: 122.10 },
  { id: 'p6', name: 'Scotiabank Mortgage',  nickname: 'Mortgage',         acct: '••••6635', cat: 'mortgage', lastPaid: '2026-05-01', lastAmount: 2410.00 },
];

const fmtCad = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
};

// Resolve the payee list for the loaded profile (plan 110). Prefer a per-persona
// `payees` block; fall back to the shared demo list when absent. Normalizes ids
// and category so hand-authored persona JSON stays terse.
const resolvePayees = (loadedProfileData) => {
  const raw = loadedProfileData?.profile?.payees ?? loadedProfileData?.payees ?? null;
  if (!Array.isArray(raw) || raw.length === 0) return INITIAL_PAYEES;
  return raw.map((p, i) => ({ cat: 'other', ...p, id: p.id || `pp${i}` }));
};

const PayeesScreen = ({ onNavigate, onBack }) => {
  const { loadedProfileData } = useProfile();
  const [payees, setPayees] = useState(() => resolvePayees(loadedProfileData));
  const [query, setQuery]   = useState('');
  const [openId, setOpenId] = useState(null);

  // Reset the (mutable) payee list whenever the loaded profile changes.
  useEffect(() => {
    setPayees(resolvePayees(loadedProfileData));
    setOpenId(null);
  }, [loadedProfileData]);

  const filtered = payees.filter(p =>
    !query.trim() ||
    p.name.toLowerCase().includes(query.toLowerCase()) ||
    p.nickname.toLowerCase().includes(query.toLowerCase())
  );

  const remove = (id) => {
    setPayees(prev => prev.filter(p => p.id !== id));
    setOpenId(null);
  };

  return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('services'))} aria-label="Back to services">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Payees</h1>
        </div>
        <button
          type="button"
          className="bh-icon-btn"
          aria-label="Add payee"
          onClick={() => onNavigate('billPayment')}
        >
          <MdAdd />
        </button>
      </header>

      <div className="bh-flow-body">
        <div className="bh-form-card" style={{ gap: 'var(--space-2xs)' }}>
          <div style={{ position: 'relative' }}>
            <MdSearch size={18} style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--bh-gray)', pointerEvents: 'none',
            }} />
            <input
              className="bh-field-input"
              type="search"
              placeholder="Search payees"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ paddingLeft: 36 }}
            />
          </div>
          <span style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
            {payees.length} saved {payees.length === 1 ? 'payee' : 'payees'}
          </span>
        </div>

        <section>
          <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Your payees</h2>
          <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
            {filtered.map((p, i) => {
              const meta = ICON_FOR(p.cat);
              const isOpen = openId === p.id;
              return (
                <div
                  key={p.id}
                  style={{
                    borderBottom: i === filtered.length - 1 ? 'none' : '1px solid var(--bh-light-gray)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : p.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
                      width: '100%', padding: 'var(--space-sm)',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      textAlign: 'left',
                    }}
                    aria-expanded={isOpen}
                  >
                    <span style={{
                      width: 36, height: 36, background: meta.tint, color: 'var(--bh-white)',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, borderRadius: 'var(--radius-md)',
                    }}>
                      <meta.Icon size={18} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ font: 'var(--bh-body)', fontWeight: 600, color: 'var(--bh-black)' }}>{p.name}</div>
                      <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                        {p.nickname} · {p.acct}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>Last</div>
                      <div style={{ font: 'var(--bh-body-sm)', fontWeight: 600, color: 'var(--bh-black)' }}>
                        {fmtCad(p.lastAmount)}
                      </div>
                      <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>{fmtDate(p.lastPaid)}</div>
                    </div>
                    <MdChevronRight
                      size={18}
                      style={{
                        color: 'var(--bh-gray)',
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0)',
                        transition: 'transform 0.15s ease',
                      }}
                    />
                  </button>
                  {isOpen && (
                    <div style={{ display: 'flex', gap: 'var(--space-2xs)', padding: '0 var(--space-sm) var(--space-sm)' }}>
                      <button
                        className="bh-btn-primary"
                        style={{ flex: 1 }}
                        onClick={() => onNavigate('billPayment')}
                      >
                        Pay now
                      </button>
                      <button
                        className="bh-btn-outline-dark"
                        style={{ flex: 1 }}
                        onClick={() => onNavigate('billPayment')}
                      >
                        <MdEdit size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        Edit
                      </button>
                      <button
                        className="bh-btn-destructive"
                        style={{ flex: 1 }}
                        onClick={() => remove(p.id)}
                      >
                        <MdDelete size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ padding: 'var(--space-md)', font: 'var(--bh-body-sm)', color: 'var(--bh-gray)', textAlign: 'center' }}>
                No payees match "{query}". Add one with the + button above.
              </div>
            )}
          </div>
        </section>

        <button
          className="bh-btn-cta"
          onClick={() => onNavigate('billPayment')}
        >
          <MdAdd size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Add a new payee
        </button>
      </div>
    </div>
  );
};

export default PayeesScreen;
